/**
 * New Password Form Component
 * Handles NEW_PASSWORD_REQUIRED challenge from AWS Cognito
 */

import React, { useMemo, useState } from 'react';
import { AlertCircle, Eye, EyeOff, CheckCircle2, Circle, Lock, User as UserIcon } from 'lucide-react';
import { useAuth } from '../../auth/AuthContext';
import './NewPasswordForm.css';

interface NewPasswordFormProps {
    onComplete?: () => void;
    onCancel?: () => void;
}

type PasswordCheckState = {
    hasNumber: boolean;
    hasSpecial: boolean;
    hasUppercase: boolean;
    hasLowercase: boolean;
};

const PASSWORD_REQUIREMENTS: Array<{ key: keyof PasswordCheckState; label: string }> = [
    { key: 'hasNumber', label: 'Contains at least 1 number' },
    { key: 'hasSpecial', label: 'Contains at least 1 special character' },
    { key: 'hasUppercase', label: 'Contains at least 1 uppercase letter' },
    { key: 'hasLowercase', label: 'Contains at least 1 lowercase letter' },
];

const getPasswordChecks = (value: string): PasswordCheckState => ({
    hasNumber: /\d/.test(value),
    hasSpecial: /[^A-Za-z0-9]/.test(value),
    hasUppercase: /[A-Z]/.test(value),
    hasLowercase: /[a-z]/.test(value),
});

const meetsPasswordRequirements = (value: string) => {
    const checks = getPasswordChecks(value);
    return Object.values(checks).every(Boolean);
};

const NewPasswordForm: React.FC<NewPasswordFormProps> = ({
    onComplete,
    onCancel
}) => {
    const [formData, setFormData] = useState({
        name: '',
        newPassword: '',
        confirmPassword: '',
    });
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const { respondToNewPasswordChallenge } = useAuth();

    // Form validation
    const validateForm = (): boolean => {
        const errors: Record<string, string> = {};

        // Name is required
        if (!formData.name.trim()) {
            errors.name = 'Name is required';
        }

        if (!formData.newPassword) {
            errors.newPassword = 'New password is required';
        } else if (formData.newPassword.length < 8) {
            errors.newPassword = 'Password must be at least 8 characters long';
        } else if (!meetsPasswordRequirements(formData.newPassword)) {
            errors.newPassword = 'Must include uppercase, lowercase, number, and special character';
        }

        if (!formData.confirmPassword) {
            errors.confirmPassword = 'Please confirm your password';
        } else if (formData.newPassword !== formData.confirmPassword) {
            errors.confirmPassword = 'Passwords do not match';
        }

        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    };

    // Handle form submission
    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setIsLoading(true);
        setError(null);

        try {
            // Since name is now required, we always include it
            const userAttributes = {
                name: formData.name.trim()
            };

            await respondToNewPasswordChallenge(formData.newPassword, userAttributes);
            onComplete?.();
        } catch (err: any) {
            setError(err.message || 'Failed to set new password');
        } finally {
            setIsLoading(false);
        }
    };

    // Validate individual field
    const validateField = (name: string, value: string) => {
        const errors: Record<string, string> = { ...validationErrors };

        if (name === 'name') {
            if (!value.trim()) {
                errors.name = 'Name is required';
            } else {
                delete errors.name;
            }
        } else if (name === 'newPassword') {
            if (!value) {
                errors.newPassword = 'New password is required';
            } else if (value.length < 8) {
                errors.newPassword = 'Password must be at least 8 characters long';
            } else if (!meetsPasswordRequirements(value)) {
                errors.newPassword = 'Must include uppercase, lowercase, number, and special character';
            } else {
                delete errors.newPassword;
            }
            // Re-validate confirm password if it has a value
            if (formData.confirmPassword) {
                if (formData.confirmPassword !== value) {
                    errors.confirmPassword = 'Passwords do not match';
                } else {
                    delete errors.confirmPassword;
                }
            }
        } else if (name === 'confirmPassword') {
            if (!value) {
                errors.confirmPassword = 'Please confirm your password';
            } else if (formData.newPassword !== value) {
                errors.confirmPassword = 'Passwords do not match';
            } else {
                delete errors.confirmPassword;
            }
        }

        setValidationErrors(errors);
    };

    const passwordChecks = useMemo(() => getPasswordChecks(formData.newPassword), [formData.newPassword]);

    // Handle input changes
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value,
        }));

        // Validate field in real-time
        validateField(name, value);
    };

    return (
        <div className="new-password-form">
            <div className="form-header">
                <h2>Set New Password</h2>
                <p>Please set a new password and provide your name for your account</p>
            </div>

            <form onSubmit={handleSubmit} className="form">
                {/* Name Field */}
                <div className="form-group">
                    <label htmlFor="name" className="form-label">
                        Full Name *
                    </label>
                    <div className="input-container">
                        <UserIcon className="input-icon" size={20} />
                        <input
                            type="text"
                            id="name"
                            name="name"
                            value={formData.name}
                            onChange={handleInputChange}
                            className={`form-input with-icon ${validationErrors.name ? 'error' : ''}`}
                            placeholder="Enter your full name"
                            disabled={isLoading}
                        />
                    </div>
                    {validationErrors.name && (
                        <span className="form-error-message">
                            {validationErrors.name}
                        </span>
                    )}
                </div>

                {/* New Password Field */}
                <div className="form-group">
                    <label htmlFor="newPassword" className="form-label">
                        New Password
                    </label>
                    <div className="input-container">
                        <Lock className="input-icon" size={20} />
                        <input
                            type={showPassword ? 'text' : 'password'}
                            id="newPassword"
                            name="newPassword"
                            value={formData.newPassword}
                            onChange={handleInputChange}
                            className={`form-input with-icon ${validationErrors.newPassword ? 'error' : ''}`}
                            placeholder="Enter your new password"
                            disabled={isLoading}
                        />
                        <button
                            type="button"
                            className="password-toggle"
                            onClick={() => setShowPassword(!showPassword)}
                            disabled={isLoading}
                        >
                            {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                        </button>
                    </div>
                    {validationErrors.newPassword && (
                        <span className="form-error-message">
                            {validationErrors.newPassword}
                        </span>
                    )}
                    <div className="password-requirements">
                        <span className="requirements-title">Password requirements</span>
                        <ul>
                            {PASSWORD_REQUIREMENTS.map(({ key, label }) => {
                                const met = passwordChecks[key];
                                return (
                                    <li key={key} className={`requirement-item ${met ? 'met' : ''}`}>
                                        {met ? <CheckCircle2 size={16} /> : <Circle size={16} />}
                                        <span>{label}</span>
                                    </li>
                                );
                            })}
                        </ul>
                    </div>
                </div>

                {/* Confirm Password Field */}
                <div className="form-group">
                    <label htmlFor="confirmPassword" className="form-label">
                        Confirm New Password
                    </label>
                    <div className="input-container">
                        <Lock className="input-icon" size={20} />
                        <input
                            type={showConfirmPassword ? 'text' : 'password'}
                            id="confirmPassword"
                            name="confirmPassword"
                            value={formData.confirmPassword}
                            onChange={handleInputChange}
                            className={`form-input with-icon ${validationErrors.confirmPassword ? 'error' : ''}`}
                            placeholder="Confirm your new password"
                            disabled={isLoading}
                        />
                        <button
                            type="button"
                            className="password-toggle"
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            disabled={isLoading}
                        >
                            {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                        </button>
                    </div>
                    {validationErrors.confirmPassword && (
                        <span className="form-error-message">
                            {validationErrors.confirmPassword}
                        </span>
                    )}
                </div>

                {/* Error Message */}
                {error && (
                    <div className="error-banner">
                        <AlertCircle size={20} />
                        <span>{error}</span>
                    </div>
                )}

                {/* Submit Button */}
                <button
                    type="submit"
                    className="btn btn-primary btn-full"
                    disabled={isLoading}
                >
                    {isLoading ? (
                        <>
                            <div className="spinner" />
                            Setting Password...
                        </>
                    ) : (
                        'Set New Password'
                    )}
                </button>

                {/* Cancel Button */}
                {onCancel && (
                    <div className="form-footer">
                        <button
                            type="button"
                            className="link-button"
                            onClick={onCancel}
                            disabled={isLoading}
                        >
                            Cancel
                        </button>
                    </div>
                )}
            </form>
        </div>
    );
};

export default NewPasswordForm;
